import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tab,
    Tabs,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import AddBannedWordDialog from "./AddBannedWordDialog";
import BannedService from "./BannedService";
import BannedWordMeaningDialog from "./BannedWordMeaningDialog";
import UserService from "../../../services/UserService";
import Logger from "../../../services/Logger";

export default function BannedWordsPopup({ open, onClose, language = "english" }) {
    const [bannedWords, setBannedWords] = useState([]);
    const [reload, setReload] = useState();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState(false);
    const [message, setMessage] = useState("");

    // Add word dialog state
    const [openAddDialog, setOpenAddDialog] = useState(false);

    // Confirmation dialog state
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [wordToDelete, setWordToDelete] = useState(null);

    // Word meaning dialog state
    const [openMeaningDialog, setOpenMeaningDialog] = useState(false);
    const [selectedWordForMeaning, setSelectedWordForMeaning] = useState(null);

    // Selected tab state
    const [selectedTab, setSelectedTab] = useState(0);

    // User role state
    const [isCreator, setIsCreator] = useState(false);

    const categories = [
        "profanity",
        "violence",
        "drugs",
        "offensive",
        "inappropriate",
        "high_frequency",
        "kids",
        "other"
    ];

    useEffect(() => {
        if (open) {
            setSelectedTab(0);
            BannedService.requestList(language).then((r) => {
                if (r) {
                    setBannedWords(r);
                }
            });
            // Check if user is creator
            UserService.getUserInfo().then((u) => {
                if (u?.cloudUser?.userRole === 'CREATOR') {
                    setIsCreator(true);
                } else {
                    setIsCreator(false);
                }
            }).catch((error) => {
                Logger.error("Error fetching user info:", error);
            });
        }
    }, [reload, language, open]);

    const handleAddWord = async (word, category, wordLanguage) => {
        try {
            await BannedService.addWord(category, word, "", wordLanguage);
            setOpenAddDialog(false);
            handleSnackBar("success", "Word added successfully");
            setReload(moment());
        } catch (error) {
            handleSnackBar("error", error?.message ?? "Failed to add word");
            Logger.error("Error adding word:", error);
        }
    };

    const handleDeleteWord = async (id) => {
        try {
            await BannedService.deleteWord(id);
            handleSnackBar("success", "Word deleted successfully");
            setReload(moment());
        } catch (error) {
            handleSnackBar("error", "Failed to delete word");
            Logger.error("Error deleting word:", error);
        }
    };

    const handleDeleteClick = (word, event) => {
        event.stopPropagation();
        setWordToDelete(word);
        setOpenConfirmDialog(true);
    };

    const handleWordClick = (word) => {
        setSelectedWordForMeaning(word);
        setOpenMeaningDialog(true);
    };

    const handleConfirmDelete = (id) => {
        handleDeleteWord(id);
        setOpenConfirmDialog(false);
        setWordToDelete(null);
    };

    const handleSnackBar = (severity, message) => {
        setOpenSnackbar(true);
        setSeverity(severity);
        setMessage(message);
    };

    // Format category name for display (e.g., "high_frequency" -> "High Frequency")
    const formatCategoryLabel = (category) => {
        return category
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const groupWordsByCategory = () => {
        const grouped = {};
        bannedWords.forEach(word => {
            if (!grouped[word.category]) {
                grouped[word.category] = [];
            }
            grouped[word.category].push(word);
        });
        return grouped;
    };

    const groupedWords = groupWordsByCategory();
    const selectedCategory = categories[selectedTab];

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }}>
                    Banned Words Manager
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ flexGrow: 1, p: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                            <Typography variant="body1" color="text.secondary">
                                Language: {language.charAt(0).toUpperCase() + language.slice(1)}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setOpenAddDialog(true)}
                            >
                                Add Word
                            </Button>
                        </Box>

                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                                {categories.map((category, index) => (
                                    <Tab
                                        key={category}
                                        label={`${formatCategoryLabel(category)} (${groupedWords[category]?.length || 0})`}
                                        value={index}
                                    />
                                ))}
                            </Tabs>
                        </Box>

                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {formatCategoryLabel(selectedCategory)} ({groupedWords[selectedCategory]?.length || 0})
                            </Typography>
                            {selectedCategory === 'high_frequency' && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    These words are allowed in <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>EASY</Box> puzzles.
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {groupedWords[selectedCategory]?.map((word) => (
                                    <Chip
                                        key={word.id}
                                        label={word.word}
                                        onClick={() => handleWordClick(word)}
                                        onDelete={isCreator ? undefined : (e) => handleDeleteClick(word, e)}
                                        color="error"
                                        variant="outlined"
                                        size="medium"
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                                {(!groupedWords[selectedCategory] || groupedWords[selectedCategory].length === 0) && (
                                    <Typography variant="body2" color="text.secondary">
                                        No words in this category
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            <AddBannedWordDialog
                open={openAddDialog}
                onClose={() => setOpenAddDialog(false)}
                onAdd={handleAddWord}
                language={language}
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />

            <ConfirmDialog
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                title="Delete Banned Word"
                message="Are you sure you want to delete this banned word?"
                targetName={wordToDelete?.word}
                targetId={wordToDelete?.id}
                callback={handleConfirmDelete}
            />

            <BannedWordMeaningDialog
                open={openMeaningDialog}
                onClose={() => setOpenMeaningDialog(false)}
                word={selectedWordForMeaning}
            />
        </>
    );
}

